import Handlebars from 'handlebars'
import type { PRSheetData } from '../types/salesforce'
import { getConnection, getImageBase64 } from './salesforce'
import { CATEGORY_LABELS, salesforceToRating, getSkillsByCategory } from '../types/skills'
import type { SkillCategory, SkillFieldMapping } from '../types/skills'
import { extractLanguageSkills } from '../utils/skillsHelper'
import templateSource from '../templates/prsheet.hbs?raw'

const PDF_LAMBDA_URL = import.meta.env.VITE_PDF_LAMBDA_URL || 'http://localhost:3001/generate-pdf'

// YouTube Video IDを抽出する関数
function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null

  // YouTube URL patterns
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

// YouTube サムネイルURLを生成する関数
function getYouTubeThumbnailUrl(videoId: string): string {
  // maxresdefault.jpg (1280x720) を優先的に使用
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
}

// Handlebarsヘルパーを登録
Handlebars.registerHelper('generateStars', function (rating: number = 0) {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating)
})

// テンプレートをコンパイル
const template = Handlebars.compile(templateSource)

// HTMLテンプレート生成（非同期に変更）
export async function generatePRSheetHTML(data: PRSheetData): Promise<string> {
  const {
    prSheet,
    career,
    skills,
    skillGroups,
    experiences,
    portfolioItems,
    highlightEpisodes,
    awards,
    references,
    educations,
    links,
  } = data

  // プロフィール画像をBase64に変換（Salesforce REST API経由）
  let profileImageBase64: string | null = null
  if (prSheet.ProfileImageContentDocumentId__c) {
    const conn = getConnection()
    if (conn) {
      try {
        profileImageBase64 = await getImageBase64(conn, prSheet.ProfileImageContentDocumentId__c)
      } catch (error) {
        console.error('Failed to get profile image base64:', error)
      }
    }
  }

  // PR画像をBase64に変換
  let prImageBase64: string | null = null
  if (prSheet.PRImageContentDocumentId__c) {
    const conn = getConnection()
    if (conn) {
      try {
        prImageBase64 = await getImageBase64(conn, prSheet.PRImageContentDocumentId__c)
      } catch (error) {
        console.error('Failed to get PR image base64:', error)
      }
    }
  }

  // イニシャルを生成
  const initials = (career.FirstName__c?.charAt(0) || '') + (career.LastName__c?.charAt(0) || '')

  // === 新しいスキル構造（固定スキル + 言語スキル） ===
  // PRSkillレコードから固定スキルのマップを作成（説明を取得するため）
  const fixedSkillLabels = new Set([
    'C',
    'DTM経験',
    'C++',
    'Swift',
    'PHP',
    'Java',
    'Ruby',
    'Python',
    'SQL',
    'HTML・CSS',
    'Dart',
    'Go',
    'JavaScript',
    'Rust',
    'Typescript',
    'Scala',
    'シェーディング言語/GSLS、HLSL、ShaderLabなど',
    'Kotlin',
    'p5.js',
    'Objective-C',
    'Unreal Engine',
    'Unity',
    'C#',
    'UIUXの経験',
    '映像制作経験',
  ])
  const fixedSkillsMap = new Map(
    skills.filter((s) => fixedSkillLabels.has(s.Name || '')).map((s) => [s.Name, s.Description__c || '']),
  )

  // 固定スキルをカテゴリー別に整理
  const fixedSkillCategories = (['engineering', 'game', 'creative'] as SkillCategory[])
    .map((category) => {
      const categorySkills = getSkillsByCategory(category)
      const skillsWithRatings = categorySkills
        .map((skillDef: SkillFieldMapping) => {
          const fieldValue = career[skillDef.careerField as keyof typeof career] as string | undefined
          const rating = salesforceToRating(fieldValue)
          if (rating === null) return null

          return {
            label: skillDef.label,
            rating,
            stars: '★'.repeat(rating) + '☆'.repeat(6 - rating),
            ratingLetter: ['E', 'D', 'C', 'B', 'A', 'S'][rating - 1],
            description: fixedSkillsMap.get(skillDef.label) || '',
          }
        })
        .filter((skill): skill is NonNullable<typeof skill> => skill !== null)

      return {
        categoryName: CATEGORY_LABELS[category],
        skills: skillsWithRatings,
      }
    })
    .filter((category) => category.skills.length > 0) // スキルが1つ以上あるカテゴリーのみ

  // 言語スキル（自由追加・削除可能）
  const languageSkillsList = extractLanguageSkills(skills)
  const languageSkillsWithStars = languageSkillsList.map((skill) => ({
    ...skill,
    stars: '★'.repeat(skill.Rating__c || 0) + '☆'.repeat(6 - (skill.Rating__c || 0)),
    ratingLetter: skill.Rating__c ? ['E', 'D', 'C', 'B', 'A', 'S'][skill.Rating__c - 1] : '',
  }))

  // === 旧スキル構造（後方互換性のため保持） ===
  const skillsByGroup = new Map<string, typeof skills>()
  const ungroupedSkills: typeof skills = []

  skills.forEach((skill) => {
    if (skill.SkillGroup__c) {
      if (!skillsByGroup.has(skill.SkillGroup__c)) {
        skillsByGroup.set(skill.SkillGroup__c, [])
      }
      skillsByGroup.get(skill.SkillGroup__c)!.push(skill)
    } else {
      ungroupedSkills.push(skill)
    }
  })

  const skillsWithStars = skills.map((skill) => {
    const rating = Math.min(Math.max(skill.Rating__c || 0, 0), 6) // 0-6の範囲にクランプ
    return {
      ...skill,
      stars: '★'.repeat(rating) + '☆'.repeat(Math.max(6 - rating, 0)),
      ratingPercent: (rating / 6) * 100,
    }
  })

  const groupsWithSkills = skillGroups.map((group) => ({
    ...group,
    skills: skillsWithStars.filter((skill) => skill.SkillGroup__c === group.Id),
  }))

  const ungroupedSkillsWithStars = skillsWithStars.filter((skill) => !skill.SkillGroup__c)

  // タグを配列に変換
  const tags = prSheet.Tags__c
    ? prSheet.Tags__c.split(',')
        .map((t) => t.trim())
        .filter((t) => t)
    : []

  // ポートフォリオアイテムの画像をBase64に変換
  const portfolioItemsWithImages = await Promise.all(
    (portfolioItems || []).map(async (item) => {
      let imageBase64: string | null = null
      if (item.ImageContentDocumentId__c) {
        const conn = getConnection()
        if (conn) {
          try {
            imageBase64 = await getImageBase64(conn, item.ImageContentDocumentId__c)
          } catch (error) {
            console.error('Failed to get portfolio image base64:', error)
          }
        }
      }
      return {
        ...item,
        imageBase64,
      }
    }),
  )

  // 注力エピソードの画像をBase64に変換
  const highlightEpisodesWithImages = await Promise.all(
    (highlightEpisodes || []).map(async (item) => {
      let imageBase64: string | null = null
      if (item.ImageContentDocumentId__c) {
        const conn = getConnection()
        if (conn) {
          try {
            imageBase64 = await getImageBase64(conn, item.ImageContentDocumentId__c)
          } catch (error) {
            console.error('Failed to get highlight episode image base64:', error)
          }
        }
      }
      return {
        ...item,
        imageBase64,
      }
    }),
  )

  // YouTube動画URLからサムネイルURLを生成
  let youtubeVideoId: string | null = null
  let youtubeThumbnailUrl: string | null = null
  if (prSheet.PRVideoURL__c) {
    youtubeVideoId = extractYouTubeVideoId(prSheet.PRVideoURL__c)
    if (youtubeVideoId) {
      youtubeThumbnailUrl = getYouTubeThumbnailUrl(youtubeVideoId)
    }
  }

  // Handlebarsテンプレートにデータを渡して生成
  return template({
    profileImageBase64,
    prImageBase64,
    initials,
    career,
    prSheet,
    tags,
    youtubeVideoId,
    youtubeThumbnailUrl,
    // 新しいスキル構造
    fixedSkillCategories,
    languageSkills: languageSkillsWithStars,
    hasNewSkills: fixedSkillCategories.length > 0 || languageSkillsWithStars.length > 0,
    // 旧スキル構造（後方互換性）
    skillGroups: groupsWithSkills,
    ungroupedSkills: ungroupedSkillsWithStars,
    hasSkills: skills.length > 0,
    experiences: experiences || [],
    portfolioItems: portfolioItemsWithImages,
    highlightEpisodes: highlightEpisodesWithImages,
    awards: awards || [],
    references: references || [],
    educations: educations || [],
    links: links || [],
  })
}

// PDF生成（Lambda呼び出し - PDFバイナリを返す）
export async function generatePDF(htmlContent: string): Promise<Blob> {
  const response = await fetch(PDF_LAMBDA_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      html: htmlContent,
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || 'PDF生成に失敗しました')
  }

  // PDFバイナリをBlobとして返す
  return await response.blob()
}
